/**
 * CORE-BI — shop culture, safe patch authority
 *
 * `shop_profile.shop_culture` is a single, shared, extensible JSON object.
 * Several independent capabilities keep their configuration inside it:
 *
 *   willingness_to_reassign, workload_balance_preference, …  Shop Profile
 *   outdoor_weather_settings                                 weather gating
 *   optimizer_settings                                       board optimizer
 *   pensieve_settings                                        preference learning
 *   foh_attention_policy                                     CORE-BN
 *
 * ...and nothing stops a future capability adding another key tomorrow.
 *
 * Why this module exists
 * ----------------------
 * Because a shared extensible object has exactly one safe write pattern,
 * and the codebase had three different ones.
 *
 * The Shop Profile page rebuilt `shop_culture` from the fields its own
 * form knows about and wrote the result as a whole-object replacement.
 * Every key belonging to another capability was silently deleted on every
 * ordinary "Save Profile" — not just CORE-BN's policy, but the board
 * optimizer's settings and the preference-learning settings too. Nothing
 * failed, nothing warned; the configuration was simply gone the next time
 * anything read it.
 *
 * The optimizer card merged correctly but read without checking for an
 * error, so a transient read failure collapsed the existing object to
 * `{}` and produced the same erasure by a different route.
 *
 * The rule this module makes enforceable: **a writer may only state the
 * keys it owns.** Everything else in the object survives untouched,
 * including keys this code has never heard of. A capability that wants a
 * key gone must say so deliberately.
 *
 * Concurrency
 * -----------
 * This is read-modify-write, not an atomic server-side patch. Two people
 * saving different settings pages within the same round trip can still
 * have the later write win. That window is small for settings screens and
 * the alternative — a JSONB-merge RPC — would add a migration and a second
 * persistence authority for a race this product has not observed. The
 * limitation is recorded rather than hidden; `jsonb_set`-style server-side
 * patching is the correct answer if it ever bites.
 *
 * Purity: no I/O, no clock, no React. Callers do the reading and writing.
 */

/** The parsed contents of the `shop_culture` JSON column. */
export type ShopCultureObject = Record<string, unknown>;

/**
 * A set of keys a writer intends to change.
 *
 * A value of `undefined` means **remove this key**. Any other value —
 * including `null` — means *set* it, because `null` is a legitimate
 * stored value in this object (`outdoor_weather_settings` is written as
 * `null` when the feature is off).
 */
export type ShopCulturePatch = Record<string, unknown>;

/**
 * Coerce whatever the JSON column actually held into an object we can
 * safely spread.
 *
 * The column is untyped and nullable, and a row can legitimately be
 * missing. It can also, in principle, hold a JSON array or scalar — none
 * of which is a shop culture. Every one of those cases becomes `{}` here
 * rather than throwing, because a settings save must not fail on a
 * malformed column; it must overwrite it with something well-formed.
 */
export function asShopCultureObject(raw: unknown): ShopCultureObject {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  return { ...(raw as ShopCultureObject) };
}

/**
 * Apply a patch to an existing shop culture, preserving every key the
 * patch does not mention.
 *
 * This is a SHALLOW merge, deliberately. A deep merge would mean a writer
 * could no longer replace its own nested settings object wholesale — a
 * capability removing one of its own sub-settings would find it
 * resurrected from the previous value. Each capability owns its key
 * outright and states it in full; nobody reaches inside anybody else's.
 *
 * Never mutates its inputs.
 */
export function patchShopCulture(
  existing: unknown,
  patch: ShopCulturePatch,
): ShopCultureObject {
  const next = asShopCultureObject(existing);
  if (typeof patch !== 'object' || patch === null || Array.isArray(patch)) {
    return next;
  }
  for (const key of Object.keys(patch)) {
    const value = patch[key];
    if (value === undefined) {
      // Deliberate removal. Distinct from "not mentioned", which leaves
      // the key alone, and from `null`, which stores a null.
      delete next[key];
      continue;
    }
    next[key] = value;
  }
  return next;
}

/**
 * The keys the Shop Profile form owns and may therefore state.
 *
 * Exported and pinned by test so the list cannot quietly drift away from
 * what the form actually renders. A key that belongs to another
 * capability must never appear here — that is precisely how the original
 * defect worked.
 */
export const SHOP_PROFILE_OWNED_CULTURE_KEYS: ReadonlyArray<string> =
  Object.freeze([
    'willingness_to_reassign',
    'specialist_gap_splitting',
    'workload_balance_preference',
    'comeback_nudge',
    'willingness_roll_past_eod',
    'present_vehicle_priority',
    'ticket_splitting_enabled',
    'min_split_minutes',
    'cross_tech_fitness_threshold',
    'proficiency_gap_threshold',
    'waiter_time_flex_min',
    'no_show_grace_threshold_min',
    'eod_risk_threshold_min',
    'allow_future_day_pull_forward',
    'capacity_recovery_automatic',
    'diagnostic_weight_enabled',
    'outdoor_weather_settings',
  ]);

/**
 * Guard used by the tests that protect this boundary: does a patch stay
 * within the keys its writer is entitled to state?
 *
 * A writer that patches a key it does not own is the erasure bug in a
 * different costume — it would be overwriting another capability's
 * configuration with a value it invented.
 */
export function patchKeysWithinOwnership(
  patch: ShopCulturePatch,
  ownedKeys: ReadonlyArray<string>,
): boolean {
  return Object.keys(patch ?? {}).every((k) => ownedKeys.includes(k));
}
